import React, { useEffect, useRef, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import "../AdminDashboard/AdminDashboard.css";
import "./GymAssets.css";
import {
  ASSET_CATEGORIES,
  JOB_TYPES,
  formatDate,
  hoursLabel,
  jobTypeLabel,
  money,
  statusLabel,
} from "./gymAssetOptions";

const emptyFilters = { q: "", branch_id: "", category: "", status: "" };
const emptyAsset = () => ({
  name: "",
  branch_id: "",
  category: "Gym Equipment",
  location: "",
  unit_price: "",
  quantity: "1",
});
const emptyWork = () => ({
  job_type: "repair",
  title: "",
  cost: "",
  vendor: "",
  started_at: new Date().toISOString().slice(0, 16),
  ended_at: "",
  details: "",
});

export default function GymAssets() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [assetForm, setAssetForm] = useState(emptyAsset());
  const [serials, setSerials] = useState([""]);
  const [more, setMore] = useState(false);
  const [extra, setExtra] = useState({ brand: "", model: "", supplier: "", purchase_date: "", remarks: "" });
  const [work, setWork] = useState(emptyWork());
  const [focus, setFocus] = useState(null);
  const [saving, setSaving] = useState(false);
  const filtersRef = useRef(filters);
  const skipSearch = useRef(true);
  filtersRef.current = filters;

  const load = async (next) => {
    setLoading(true);
    try {
      const [summaryRes, listRes] = await Promise.all([
        UserServices.gymAssetSummary({ branch_id: next.branch_id, q: next.q }),
        UserServices.gymAssets(next),
      ]);
      setSummary(summaryRes.data?.data || null);
      setRows(listRes.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not load assets");
    } finally {
      setLoading(false);
    }
  };

  const apply = (patch) => {
    const next = { ...filtersRef.current, ...patch };
    setFilters(next);
    load(next);
  };

  const openAsset = async (row) => {
    setModal("asset");
    setFocus(row);
    try {
      const res = await UserServices.gymAsset(row.id);
      setFocus(res.data?.data || row);
    } catch (err) {
      toast.error("Could not open this asset");
    }
  };

  useEffect(() => {
    UserServices.branchNameAll().then((res) => setBranches(res.data?.data || [])).catch(() => setBranches([]));
    load(emptyFilters);
  }, []);

  useEffect(() => {
    if (skipSearch.current) {
      skipSearch.current = false;
      return;
    }
    const timer = setTimeout(() => apply({ q: query }), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const closeModal = () => {
    setModal(null);
    setSaving(false);
  };

  const saveAsset = async (event) => {
    event.preventDefault();
    if (!assetForm.name.trim() || !assetForm.branch_id || !assetForm.category) {
      toast.error("Name, branch, and category are required");
      return;
    }
    const filled = serials.map((serial) => serial.trim()).filter(Boolean);
    const payload = {
      ...assetForm,
      ...extra,
      name: assetForm.name.trim(),
      quantity: filled.length ? 1 : Number(assetForm.quantity) || 1,
      unit_price: assetForm.unit_price === "" ? null : Number(assetForm.unit_price),
      purchase_date: extra.purchase_date || null,
      serial_numbers: filled.length ? filled : undefined,
    };
    setSaving(true);
    try {
      const res = await UserServices.createGymAsset(payload);
      const body = res.data?.data || {};
      toast.success(body.count > 1 ? `Saved ${body.count} assets` : "Asset saved");
      setAssetForm(emptyAsset());
      setSerials([""]);
      setExtra({ brand: "", model: "", supplier: "", purchase_date: "", remarks: "" });
      setMore(false);
      closeModal();
      load(filtersRef.current);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not save the asset");
      setSaving(false);
    }
  };

  const saveWork = async (event) => {
    event.preventDefault();
    if (!focus?.id) return;
    if (!work.title.trim()) {
      toast.error("Give the work a short title");
      return;
    }
    setSaving(true);
    try {
      await UserServices.addGymAssetJob(focus.id, {
        ...work,
        cost: work.cost === "" ? 0 : Number(work.cost),
        started_at: work.started_at.replace("T", " "),
        ended_at: work.ended_at ? work.ended_at.replace("T", " ") : null,
        status: work.ended_at ? "done" : "open",
      });
      toast.success(work.ended_at ? "Work saved. The machine stays active if nothing else is open." : "Work opened. The machine is under repair.");
      setWork(emptyWork());
      await openAsset(focus);
      setModal("asset");
      load(filtersRef.current);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not save the work");
    } finally {
      setSaving(false);
    }
  };

  const markWorking = async () => {
    const openJobs = (focus?.jobs || []).filter((job) => job.status === "open");
    if (!openJobs.length) return;
    setSaving(true);
    try {
      const ended = new Date().toISOString().slice(0, 19).replace("T", " ");
      for (const job of openJobs) {
        await UserServices.updateGymAssetJob(job.id, { ...job, status: "done", ended_at: ended });
      }
      toast.success("Marked working");
      await openAsset(focus);
      setModal("asset");
      load(filtersRef.current);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not close the open work");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wrapper gym-assets">
      <ToastContainer />
      <main className="page-content customer-contnent">
        <div className="site-main-design ad-shell">
          <MenuButton />
          <div className="container-fluid px-3 px-md-4 pb-4">
            <div className="ad-topbar d-flex justify-content-between align-items-start gap-3">
              <div>
                <p className="ad-eyebrow">Facility</p>
                <h2 className="ad-title">Assets</h2>
                <p className="ad-subtitle">Add a machine, record the work, and mark it working again from this page.</p>
              </div>
              <button type="button" className="btn btn-danger" onClick={() => { setAssetForm(emptyAsset()); setSerials([""]); setModal("add"); }}>
                Add asset
              </button>
            </div>

            <div className="gym-kpis mb-3">
              <button type="button" className={`gym-kpi ${!filters.status ? "is-on" : ""}`} onClick={() => apply({ status: "" })}>
                <span>Units</span><strong>{summary?.units ?? "—"}</strong>
              </button>
              <button type="button" className={`gym-kpi ${filters.status === "active" ? "is-on" : ""}`} onClick={() => apply({ status: filters.status === "active" ? "" : "active" })}>
                <span>Working</span><strong>{summary?.working ?? "—"}</strong>
              </button>
              <button type="button" className={`gym-kpi is-alert ${filters.status === "under_repair" ? "is-on" : ""}`} onClick={() => apply({ status: filters.status === "under_repair" ? "" : "under_repair" })}>
                <span>Under repair</span><strong>{summary?.under_repair ?? "—"}</strong>
              </button>
              <div className="gym-kpi"><span>Repair cost</span><strong>{summary ? money(summary.maintenance_cost) : "—"}</strong></div>
              <div className="gym-kpi"><span>Downtime</span><strong>{summary ? hoursLabel(summary.downtime_minutes) : "—"}</strong></div>
            </div>

            <section className="gym-panel p-3">
              <div className="gym-filters mb-3">
                <input className="form-control" placeholder="Search name, serial, or code" value={query} onChange={(event) => setQuery(event.target.value)} />
                <select className="form-select" value={filters.branch_id} onChange={(event) => apply({ branch_id: event.target.value })}>
                  <option value="">All branches</option>
                  {branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}
                </select>
                <select className="form-select" value={filters.category} onChange={(event) => apply({ category: event.target.value })}>
                  <option value="">All categories</option>
                  {ASSET_CATEGORIES.map((category) => <option key={category}>{category}</option>)}
                </select>
              </div>
              <div className="table-responsive">
                <table className="table gym-table mb-0">
                  <thead>
                    <tr>
                      <th>Asset</th>
                      <th>Where</th>
                      <th>Status</th>
                      <th>Repair cost</th>
                      <th>Downtime</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? <tr><td colSpan="6">Loading assets...</td></tr> : rows.length === 0 ? (
                      <tr><td colSpan="6">No assets match. Add the first one.</td></tr>
                    ) : rows.map((row) => (
                      <tr key={row.id} onClick={() => openAsset(row)}>
                        <td>
                          <div className="gym-asset-name">{row.name}</div>
                          <div className="gym-asset-meta">{[row.serial_number || (row.quantity > 1 ? `Qty ${row.quantity}` : "No serial"), row.asset_code].filter(Boolean).join(" · ")}</div>
                        </td>
                        <td>
                          <div>{row.branch_name || "—"}</div>
                          <div className="gym-asset-meta">{row.location || row.category}</div>
                        </td>
                        <td><span className={`gym-pill is-${row.status}`}>{statusLabel(row.status)}</span></td>
                        <td>{money(row.maintenance_cost)}</td>
                        <td>{hoursLabel(row.downtime_minutes)}</td>
                        <td>
                          {row.status !== "disposed" && row.status !== "missing" ? (
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={(event) => { event.stopPropagation(); setFocus(row); setWork(emptyWork()); setModal("work"); }}>
                              Add work
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>
      </main>

      {modal ? (
        <div className="gym-modal-back" onMouseDown={closeModal}>
          <div className={`gym-modal ${modal === "asset" ? "is-wide" : ""}`} onMouseDown={(event) => event.stopPropagation()}>
            {modal === "add" ? (
              <form onSubmit={saveAsset}>
                <div className="gym-panel-head"><h3>Add asset</h3></div>
                <div className="gym-form-grid">
                  <label className="gym-field">Name<input className="form-control" value={assetForm.name} onChange={(event) => setAssetForm({ ...assetForm, name: event.target.value })} /></label>
                  <label className="gym-field">Branch
                    <select className="form-select" value={assetForm.branch_id} onChange={(event) => setAssetForm({ ...assetForm, branch_id: event.target.value })}>
                      <option value="">Select branch</option>
                      {branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}
                    </select>
                  </label>
                  <label className="gym-field">Category
                    <select className="form-select" value={assetForm.category} onChange={(event) => setAssetForm({ ...assetForm, category: event.target.value })}>
                      {ASSET_CATEGORIES.map((category) => <option key={category}>{category}</option>)}
                    </select>
                  </label>
                  <label className="gym-field">Location<input className="form-control" value={assetForm.location} onChange={(event) => setAssetForm({ ...assetForm, location: event.target.value })} /></label>
                  <label className="gym-field">Unit price<input className="form-control" type="number" min="0" value={assetForm.unit_price} onChange={(event) => setAssetForm({ ...assetForm, unit_price: event.target.value })} /></label>
                  {serials.some((serial) => serial.trim()) ? null : (
                    <label className="gym-field">Quantity<input className="form-control" type="number" min="1" value={assetForm.quantity} onChange={(event) => setAssetForm({ ...assetForm, quantity: event.target.value })} /></label>
                  )}
                  <div className="gym-field gym-span-2">
                    <span>Serial numbers</span>
                    <p className="gym-muted">One asset is created for each serial. Leave blank for a single quantity.</p>
                    <div className="gym-serials">
                      {serials.map((serial, index) => (
                        <div className="gym-serial-row" key={index}>
                          <input className="form-control" placeholder={`Serial ${index + 1}`} value={serial} onChange={(event) => setSerials((current) => current.map((item, itemIndex) => itemIndex === index ? event.target.value : item))} />
                        </div>
                      ))}
                    </div>
                    <button type="button" className="btn btn-link px-0" onClick={() => setSerials((current) => [...current, ""])}>Add another serial</button>
                  </div>
                </div>
                <button type="button" className="btn btn-link px-0" onClick={() => setMore((open) => !open)}>{more ? "Hide extra details" : "Add extra details"}</button>
                {more ? (
                  <div className="gym-form-grid mb-3">
                    <label className="gym-field">Brand<input className="form-control" value={extra.brand} onChange={(event) => setExtra({ ...extra, brand: event.target.value })} /></label>
                    <label className="gym-field">Model<input className="form-control" value={extra.model} onChange={(event) => setExtra({ ...extra, model: event.target.value })} /></label>
                    <label className="gym-field">Supplier<input className="form-control" value={extra.supplier} onChange={(event) => setExtra({ ...extra, supplier: event.target.value })} /></label>
                    <label className="gym-field">Purchase date<input className="form-control" type="date" value={extra.purchase_date} onChange={(event) => setExtra({ ...extra, purchase_date: event.target.value })} /></label>
                    <label className="gym-field gym-span-2">Remarks<textarea className="form-control" rows="2" value={extra.remarks} onChange={(event) => setExtra({ ...extra, remarks: event.target.value })} /></label>
                  </div>
                ) : null}
                <div className="gym-actions mt-3">
                  <button type="button" className="btn btn-outline-secondary" onClick={closeModal}>Cancel</button>
                  <button className="btn btn-danger" disabled={saving} type="submit">{saving ? "Saving..." : "Save"}</button>
                </div>
              </form>
            ) : null}

            {modal === "work" ? (
              <form onSubmit={saveWork}>
                <div className="gym-panel-head">
                  <div>
                    <h3>Record work</h3>
                    <p>{focus?.name} · {focus?.serial_number || focus?.asset_code}</p>
                  </div>
                </div>
                <div className="gym-form-grid">
                  <label className="gym-field">Type
                    <select className="form-select" value={work.job_type} onChange={(event) => setWork({ ...work, job_type: event.target.value })}>
                      {JOB_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </label>
                  <label className="gym-field">Title<input className="form-control" value={work.title} onChange={(event) => setWork({ ...work, title: event.target.value })} /></label>
                  <label className="gym-field">Cost (PKR)<input className="form-control" type="number" min="0" value={work.cost} onChange={(event) => setWork({ ...work, cost: event.target.value })} /></label>
                  <label className="gym-field">Vendor<input className="form-control" value={work.vendor} onChange={(event) => setWork({ ...work, vendor: event.target.value })} /></label>
                  <label className="gym-field">Started<input className="form-control" type="datetime-local" value={work.started_at} onChange={(event) => setWork({ ...work, started_at: event.target.value })} /></label>
                  <label className="gym-field">Ended<input className="form-control" type="datetime-local" value={work.ended_at} onChange={(event) => setWork({ ...work, ended_at: event.target.value })} /></label>
                  <label className="gym-field gym-span-2">What was done<textarea className="form-control" rows="2" value={work.details} onChange={(event) => setWork({ ...work, details: event.target.value })} /></label>
                </div>
                <p className="gym-muted mt-2">Leave Ended empty while it is still down. Fill Ended when the work is finished.</p>
                <div className="gym-actions mt-3">
                  <button type="button" className="btn btn-outline-secondary" onClick={closeModal}>Cancel</button>
                  <button className="btn btn-danger" disabled={saving} type="submit">{saving ? "Saving..." : "Save work"}</button>
                </div>
              </form>
            ) : null}

            {modal === "asset" && focus ? (
              <div>
                <div className="gym-panel-head">
                  <div>
                    <h3>{focus.name}</h3>
                    <p>{focus.asset_code} · {focus.serial_number || `Qty ${focus.quantity}`} · {focus.branch_name}</p>
                  </div>
                  <span className={`gym-pill is-${focus.status}`}>{statusLabel(focus.status)}</span>
                </div>
                <div className="gym-kpis mb-3">
                  <div className="gym-kpi"><span>Repair cost</span><strong>{money(focus.maintenance_cost)}</strong></div>
                  <div className="gym-kpi"><span>Downtime</span><strong>{hoursLabel(focus.downtime_minutes)}</strong></div>
                  <div className="gym-kpi"><span>Open jobs</span><strong>{(focus.jobs || []).filter((job) => job.status === "open").length}</strong></div>
                </div>
                <div className="gym-actions mb-3">
                  {focus.status !== "disposed" && focus.status !== "missing" ? (
                    <button type="button" className="btn btn-danger" onClick={() => { setWork(emptyWork()); setModal("work"); }}>Add work</button>
                  ) : null}
                  {(focus.jobs || []).some((job) => job.status === "open") ? (
                    <button type="button" className="btn btn-outline-secondary" disabled={saving} onClick={markWorking}>Mark working</button>
                  ) : null}
                  <button type="button" className="btn btn-outline-secondary" onClick={closeModal}>Close</button>
                </div>
                <table className="table gym-table mb-0">
                  <thead><tr><th>Work</th><th>When</th><th>Cost</th><th>Downtime</th><th>Status</th></tr></thead>
                  <tbody>
                    {(focus.jobs || []).length === 0 ? <tr><td colSpan="5">No work yet.</td></tr> : focus.jobs.map((job) => (
                      <tr key={job.id}>
                        <td><div className="gym-asset-name">{job.title}</div><div className="gym-asset-meta">{jobTypeLabel(job.job_type)}</div></td>
                        <td>{formatDate(job.started_at)}{job.ended_at ? ` – ${formatDate(job.ended_at)}` : ""}</td>
                        <td>{money(job.cost)}</td>
                        <td>{hoursLabel(job.downtime_minutes)}</td>
                        <td>{job.status === "open" ? "Open" : "Done"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
